import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select, Store } from '@ngxs/store';
import {
  PersonalDetailsWithFullName,
  PersonalizationPerson,
  ProductState,
  SetAdditionalPersons,
  ShoppingCartPersonalizationState
} from '@platform-frontend/checkout';
import { InclusiveActionFilter } from '@platform-frontend/ui-tools';
import { Navigate } from '@platform-frontend/util';
import { filter, map, Observable, of, switchMap, take, tap, withLatestFrom } from 'rxjs';

@UntilDestroy()
@Component({
  selector: 'yat-multiple-person',
  templateUrl: './multiple-person.component.html',
  styleUrls: ['./multiple-person.component.scss']
})
export class MultiplePersonComponent implements OnInit {
  constructor(private activatedRoute: ActivatedRoute, private fb: FormBuilder, private store: Store) {}

  @Select(ShoppingCartPersonalizationState.firstPerson)
  firstPerson$: Observable<PersonalizationPerson<string | null> | null>;

  @Select(ProductState.maxNumberOfPersons)
  maxNumberOfPersons$: Observable<number>;

  multiplePersonForm = this.fb.group({
    persons: this.fb.array<FormControl<string | null>>([])
  });

  setAdditionalPersonsActionFilter = new InclusiveActionFilter(SetAdditionalPersons);
  focused: number | null = null;
  maxPersons$: Observable<number> = of(0);

  get persons(): FormArray<FormControl<string | null>> {
    return this.multiplePersonForm.controls.persons;
  }

  ngOnInit(): void {
    this.maxPersons$ = this.store.select(ProductState.maxNumberOfPersons).pipe(map(maxNumberOfPersons => (maxNumberOfPersons ? maxNumberOfPersons - 1 : 0)));

    const additionalPersons = this.store.selectSnapshot(ShoppingCartPersonalizationState.additionalPersons);
    additionalPersons.forEach((person: PersonalDetailsWithFullName<string, Date>) => {
      this.persons.push(this.createPersonControl(person.fullName));
    });

    this.activatedRoute.queryParams
      .pipe(
        take(1),
        withLatestFrom(this.maxPersons$),
        filter(([params, maxPersons]) => params['populateInput'] === 'true' && this.persons.length < maxPersons),
        tap(() => this.persons.push(this.createPersonControl()))
      )
      .subscribe();
  }

  createPersonControl(value: string | null = null): FormControl<string | null> {
    return this.fb.control<string | null>(value);
  }

  addPerson(): void {
    this.persons.push(this.createPersonControl());
  }

  removePerson(index: number): void {
    this.persons.removeAt(index);
  }

  submitForm(value: { persons: string[] }): void {
    const additionalPersons: PersonalDetailsWithFullName<string, Date>[] = value.persons
      .filter(person => person !== null && person.trim().length !== 0)
      .map(person => {
        return {
          firstName: null,
          lastName: null,
          fullName: person,
          birthDate: null,
          address: null
        };
      });

    this.store
      .dispatch(new SetAdditionalPersons(additionalPersons))
      .pipe(
        switchMap(() => this.store.dispatch(new Navigate(['../overview'], { relativeTo: this.activatedRoute }))),
        untilDestroyed(this)
      )
      .subscribe();
  }

  cancel(): void {
    this.store.dispatch(new Navigate(['../overview'], { relativeTo: this.activatedRoute }));
  }

  editFirstPerson(): void {
    this.store.dispatch(new Navigate(['../create-person'], { relativeTo: this.activatedRoute }));
  }
}
